import { Component } from 'react';
import { CssGeneratorLayout, cssGenCommon as genStyles } from '../converter-layout/css-generator-layout';

const PRESETS: Record<string, string> = {
    'fade-in': '0% { opacity: 0; }\n  100% { opacity: 1; }',
    'slide-up': '0% { transform: translateY(20px); opacity: 0; }\n  100% { transform: translateY(0); opacity: 1; }',
    'bounce': '0%, 20%, 50%, 80%, 100% { transform: translateY(0); }\n  40% { transform: translateY(-30px); }\n  60% { transform: translateY(-15px); }',
    'pulse': '0%, 100% { transform: scale(1); }\n  50% { transform: scale(1.1); }',
    'shake': '0%, 100% { transform: translateX(0); }\n  10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); }\n  20%, 40%, 60%, 80% { transform: translateX(10px); }',
    'spin': '0% { transform: rotate(0deg); }\n  100% { transform: rotate(360deg); }',
};

interface State {
    preset: string;
    keyframes: string;
    duration: number;
    delay: number;
    iteration: string;
    timing: string;
    direction: string;
    fillMode: string;
    copied: boolean;
}

export default class CssAnimationGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        preset: 'pulse',
        keyframes: PRESETS.pulse,
        duration: 2,
        delay: 0,
        iteration: 'infinite',
        timing: 'ease-in-out',
        direction: 'normal',
        fillMode: 'none',
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private get cssCode(): string {
        const { preset, keyframes, duration, delay, iteration, timing, direction, fillMode } = this.state;
        return `@keyframes ${preset} {\n  ${keyframes}\n}\n\n.target {\n  animation-name: ${preset};\n  animation-duration: ${duration}s;\n  animation-delay: ${delay}s;\n  animation-iteration-count: ${iteration};\n  animation-timing-function: ${timing};\n  animation-direction: ${direction};\n  animation-fill-mode: ${fillMode};\n}`;
    }

    selectPreset = (key: string) => {
        this.setState({ preset: key, keyframes: PRESETS[key] });
    };

    copyCss = () => {
        navigator.clipboard.writeText(this.cssCode).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { preset, keyframes, duration, delay, iteration, timing, direction, fillMode, copied } = this.state;
        const animStyle: React.CSSProperties = {
            animation: `${preset} ${duration}s ${timing} ${delay}s ${iteration === 'infinite' ? 'infinite' : iteration} ${direction} ${fillMode}`,
            width: 80, height: 80, background: '#6366f1', borderRadius: 8,
        };
        const styleTag = `@keyframes ${preset} { ${keyframes} }`;

        const controls = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Preset</span></div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                        {Object.keys(PRESETS).map(p => (
                            <button key={p}
                                className={`${genStyles.iconBtn} ${preset === p ? genStyles.iconBtnPrimary : ''}`}
                                onClick={() => this.selectPreset(p)}>{p}</button>
                        ))}
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="ca-kf">Keyframes (inside @keyframes)</label>
                        <textarea id="ca-kf" rows={5} value={keyframes} spellCheck={false}
                            onChange={e => this.setState({ keyframes: e.target.value })} />
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Timing</span></div>
                    <div className={genStyles.field}>
                        <label>Duration: <strong>{duration}s</strong></label>
                        <input type="range" min={0.1} max={10} step={0.1} value={duration}
                            onChange={e => this.setState({ duration: +e.target.value })} />
                    </div>
                    <div className={genStyles.field}>
                        <label>Delay: <strong>{delay}s</strong></label>
                        <input type="range" min={0} max={5} step={0.1} value={delay}
                            onChange={e => this.setState({ delay: +e.target.value })} />
                    </div>
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="ca-timing">Timing function</label>
                            <select id="ca-timing" value={timing}
                                onChange={e => this.setState({ timing: e.target.value })}>
                                <option>ease</option><option>linear</option><option>ease-in</option>
                                <option>ease-out</option><option>ease-in-out</option><option>cubic-bezier(.68, -.55, .27, 1.55)</option>
                            </select>
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="ca-iter">Iteration</label>
                            <input id="ca-iter" type="text" value={iteration}
                                onChange={e => this.setState({ iteration: e.target.value })} />
                        </div>
                    </div>
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="ca-dir">Direction</label>
                            <select id="ca-dir" value={direction}
                                onChange={e => this.setState({ direction: e.target.value })}>
                                <option>normal</option><option>reverse</option><option>alternate</option><option>alternate-reverse</option>
                            </select>
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="ca-fill">Fill mode</label>
                            <select id="ca-fill" value={fillMode}
                                onChange={e => this.setState({ fillMode: e.target.value })}>
                                <option>none</option><option>forwards</option><option>backwards</option><option>both</option>
                            </select>
                        </div>
                    </div>
                </div>
            </>
        );

        return (
            <CssGeneratorLayout
                controls={controls}
                previewLabel="Preview"
                previewMeta={preset}
                preview={
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 160 }}>
                        <style>{styleTag}</style>
                        <div style={animStyle} />
                    </div>
                }
                codeLabel="CSS"
                codeMeta={`${this.cssCode.length} chars`}
                code={this.cssCode}
                codeActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyCss}>
                        {copied ? '✓ Copied' : '📋 Copy CSS'}
                    </button>
                }
            />
        );
    }
}
